import { Dataset } from '../models/crd-state.interface';

/** Null, undefined and blank all mean "no org id". */
export function normalizeOrgId(
  id: string | number | null | undefined
): string | null {
  if (id === null || id === undefined) {
    return null;
  }

  const trimmed = String(id).trim();
  return trimmed === '' ? null : trimmed;
}

export function isSameOrg(
  dataset: Dataset,
  companyId: number | undefined
): boolean {
  const datasetOrg = normalizeOrgId(dataset.agd_company_id);
  const userOrg = normalizeOrgId(companyId);

  return datasetOrg !== null && datasetOrg === userOrg;
}

/**
 * Decides which dataset to open, in order:
 *
 *   1. the remembered choice, if still in the list;
 *   2. the first dataset owned by the logged-in org;
 *   3. the first dataset in the list.
 *
 * Membership in the list is itself the authorization check — the backend scopes
 * it to the user's active roles — so a remembered choice is honoured whatever
 * org owns it.
 *
 * Rules 2 and 3 are positional: they take the server's order, which Postgres
 * produces under the database collation (`en_US.UTF8`). Do not sort this list
 * client-side — no JS comparator reproduces glibc's ordering, `localeCompare`
 * included, so a sort would silently change which dataset some users open.
 */
export function resolveDefaultDataset(
  datasets: Dataset[],
  companyId: number | undefined,
  rememberedName: string | null
): Dataset | undefined {
  if (!datasets?.length) {
    return undefined;
  }

  if (rememberedName) {
    const remembered = datasets.find(
      (dataset) => dataset.name === rememberedName
    );

    if (remembered) {
      return remembered;
    }
  }

  return (
    datasets.find((dataset) => isSameOrg(dataset, companyId)) ?? datasets[0]
  );
}
